import { useEffect, useState } from "react";
import { Scale, Star } from "lucide-react";
import { toast } from "sonner";
import { fetchRating, submitRating } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import LanguageSelect from "@/components/LanguageSelect";

function RatingWidget() {
  const { lang, t } = useLang();
  const [mine, setMine] = useState(() => {
    try {
      return Number(localStorage.getItem("ws_rated")) || 0;
    } catch {
      return 0;
    }
  });
  const [avg, setAvg] = useState(null);
  const [hover, setHover] = useState(0);

  useEffect(() => {
    fetchRating().then(setAvg).catch(() => {});
  }, []);

  const submit = async (s) => {
    if (mine) return;
    try {
      const r = await submitRating(s);
      setAvg(r);
      setMine(s);
      localStorage.setItem("ws_rated", String(s));
      toast.success(t("foot.thanks"));
    } catch {
      toast.error(t("dl.serverFail"));
    }
  };

  return (
    <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="rating-widget">
      <span className="text-xs font-bold text-muted-foreground">{t("foot.rate")}</span>
      <div className="flex items-center gap-1" dir="ltr">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            data-testid={`rating-star-${s}`}
            onClick={() => submit(s)}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            disabled={mine > 0}
            className="transition-transform duration-150 hover:scale-125 active:scale-95 disabled:cursor-default"
            aria-label={`${s} stars`}
          >
            <Star
              className={`h-5 w-5 ${
                s <= (hover || mine)
                  ? "fill-neon text-neon drop-shadow-[0_0_6px_var(--c-neon-soft)]"
                  : "text-muted-foreground/50"
              }`}
            />
          </button>
        ))}
      </div>
      {avg && avg.count > 0 && (
        <span className="text-xs text-neon" data-testid="rating-average">
          {t("foot.avg")} {Number(avg.average).toLocaleString(lang)} · {Number(avg.count).toLocaleString(lang)} {t("foot.ratings")}
        </span>
      )}
    </div>
  );
}

export default function Footer() {
  const { lang, t } = useLang();
  const prefix = lang === "en" ? "/en" : "";
  const home = lang === "en" ? "/en/" : "/";
  const quick = [
    { key: "tiktok", href: `${home}#downloader`, label: t("nav.tiktok") },
    { key: "guides", href: `${prefix}/guides`, label: t("foot.guides") },
    { key: "faq", href: `${home}#faq`, label: t("foot.faq") },
    { key: "sitemap", href: "/sitemap.xml", label: t("foot.sitemap") },
  ];
  const company = [
    { key: "about", href: `${prefix}/about`, label: t("foot.about") },
    { key: "contact", href: `${prefix}/contact`, label: t("foot.contact") },
  ];
  const legal = [
    { key: "privacy", href: `${prefix}/privacy-policy`, label: t("foot.privacy") },
    { key: "terms", href: `${prefix}/terms-of-service`, label: t("foot.terms") },
    { key: "cookies", href: `${prefix}/cookie-policy`, label: t("foot.cookies") },
    { key: "dmca", href: `${prefix}/dmca`, label: t("foot.dmca") },
  ];

  return (
    <footer className="relative border-t border-white/10 pt-20 pb-10 overflow-hidden" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2 mb-5">
              <img src="/logo192.webp" alt="SS Downloader" width="36" height="36" className="h-9 w-9 rounded-xl" data-testid="footer-logo-icon" />
              <span className="font-display text-2xl font-black pt-0.5">
                <span className="neon-text-gradient">SS</span> <span className="text-ice">Downloader</span>
              </span>
            </div>
            <p className="max-w-xs text-sm text-muted-foreground leading-relaxed">{t("foot.desc")}</p>
            <RatingWidget />
          </div>

          {[
            [t("foot.quickCol"), quick],
            [t("foot.companyCol"), company],
            [t("foot.legalCol"), legal],
          ].map(([title, links]) => (
            <nav key={title} aria-label={title}>
              <h3 className="text-xs font-bold tracking-[0.2em] text-neon mb-4">{title}</h3>
              <ul className="space-y-2.5 text-sm font-bold text-muted-foreground">
                {links.map((l) => (
                  <li key={l.key}>
                    <a href={l.href} data-testid={`footer-${l.key}-link`} className="transition-colors duration-200 hover:text-neon">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-10 flex items-start gap-2 rounded-xl border border-white/10 bg-surface/50 p-4 text-xs text-muted-foreground max-w-xl">
          <Scale className="h-4 w-4 shrink-0 mt-0.5 text-neon" />
          <p>{t("foot.legal")}</p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <LanguageSelect />
          <p className="text-xs text-muted-foreground/60">
            SS Downloader © {new Date().getFullYear()} — {t("foot.made")}
          </p>
        </div>
      </div>
    </footer>
  );
}
